import { defineStore } from 'pinia'
import { FLOW_TEMPLATE_EVENT_NAMES, flowTemplateEventType } from '#shared/flow-template-events'
import type { FlowTemplateType } from '~~/prisma/generated/browser'

export type { FlowTemplateType }

export interface UICommand {
    label: string
    action: string
}

export interface UIStepValue {
    key: string
    value: string | null
}

export interface UIConditionResult {
    conditionId: number
    sourceType: string
    sourceKey: string
    operator: string
    compareValue: string
    rawValue: string | null
    result: 'pass' | 'fail' | 'skip'
    handler: 'step' | 'iteration' | 'while'
}

export interface UITreeNode {
    key: string
    label: string
    position: number
    ordinal?: number
    icon: string
    iconColor: string
    nodeType: 'step' | 'attempt'
    sourceId: number
    action?: string
    canHaveChildren?: boolean
    isIterator?: boolean
    errorMessage?: string
    inputs: UIStepValue[]
    outputs: UIStepValue[]
    conditions: UIConditionResult[]
    commands: UICommand[]
    header: string
    body: string | null
    children: UITreeNode[]
    lazy: boolean
    parentKey: string | null
    results: any[]
}

export interface TemplateKeyConflict {
    stepId: number
    keys: string[]
}

export interface TemplateKeyRun {
    running: boolean
    applied: number
    skipped: number
    conflicts: TemplateKeyConflict[]
}

function emptyTemplateKeyRun(): TemplateKeyRun {
    return {
        running: false,
        applied: 0,
        skipped: 0,
        conflicts: [],
    }
}

const AUTO_EXPAND_STATUSES = new Set(['running', 'failed', 'awaiting_user', 'blocked', 'suspended'])

const STATUS_ICONS: Record<string, string> = {
    pending: 'radio_button_unchecked',
    running: 'sync',
    completed: 'check_circle',
    failed: 'error',
    skipped: 'skip_next',
    suspended: 'pause_circle',
    blocked: 'block',
    awaiting_user: 'person',
    cancelled: 'cancel',
    help: 'help',
}

const STATUS_ICON_COLORS: Record<string, string> = {
    pending: 'grey-6',
    running: 'blue-8',
    completed: 'green-8',
    failed: 'red-8',
    skipped: 'grey-5',
    suspended: 'orange-8',
    blocked: 'red-9',
    awaiting_user: 'orange-7',
    cancelled: 'grey-6',
}

function stepIcon(status: string = 'help', variant?: 'outline'): string {
    const baseIcon = STATUS_ICONS[status]
    const icon = [baseIcon, variant]
        .filter(Boolean)
        .join('_')
    return icon
}

function attemptIcon(status?: string): string {
    return stepIcon(status, 'outline')
}

function stepIconColor(status: string): string {
    return STATUS_ICON_COLORS[status] ?? 'grey-6'
}

function stepCommands(status: string): UICommand[] {
    if (status === 'failed') {
        return [
            {
                label: 'Retry',
                action: 'retry',
            },
            {
                label: 'Skip',
                action: 'skip',
            },
        ]
    }
    if (status === 'awaiting_user') {
        return [
            {
                label: 'Mark done',
                action: 'mark-done',
            },
        ]
    }
    return []
}

function stepBody(status: string): string | null {
    return status === 'awaiting_user' ? 'step' : null
}

function splitValues(values: Array<{ direction: string; key: string; value: string | null }>): {
    inputs: UIStepValue[]
    outputs: UIStepValue[]
} {
    return {
        inputs: values
            .filter((v) => v.direction === 'input')
            .map((v) => ({ key: v.key, value: v.value })),
        outputs: values
            .filter((v) => v.direction === 'output' && v.key !== '__prompt__')
            .map((v) => ({ key: v.key, value: v.value })),
    }
}

function flattenNode(node: UITreeNode): UITreeNode {
    return {
        ...node,
        children: node.children.map(flattenNode),
    }
}

export interface FlowStep {
    id: number
    parentFlowStepId: number | null
    [key: string]: unknown
}

export interface UnsetFlowProfile {
    inputName: string,
    sourceKey: string,
    provider: string,
    defaultValue: string,
}

export interface FlowPreconditionCheck {
    id: number,
    checkType: 'input_mapping' | 'type_compatibility' | 'template_profile' | 'flow_profile' | 'profile_option',
    target: string,
    status: 'passed' | 'failed',
    message: string | null,
}

interface PendingProfilePrompt {
    inputs: UnsetFlowProfile[],
    body: { templateId?: number, templateName?: string },
    context: Record<string, any>,
    propagate: boolean,
    resolve: (result: { flowId: number } | undefined) => void,
}

/**
 * Computes the `binding = flow` profile inputs of a template that are still unset
 * (not already supplied in `context`). Each carries its provider and the template-
 * assigned default value (if any), used to seed the trigger-time profile popup.
 */
export function computeUnsetFlowProfiles(
    template: any,
    actions: any[],
    context: Record<string, any> = {},
): UnsetFlowProfile[] {
    const result: UnsetFlowProfile[] = []
    const seen = new Set<string>()

    for (const step of template?.steps ?? []) {
        const action = (actions ?? []).find((a: any) => a.name === step.action)
        if (!action) {
            continue
        }
        const inputDefinitions = new Map<string, any>((action.inputs ?? []).map((i: any) => [i.name, i]))

        for (const mapping of step.stepInputMappings ?? []) {
            if ((mapping.binding ?? 'template') !== 'flow') {
                continue
            }
            const definition = inputDefinitions.get(mapping.inputName)
            if (!definition || definition.type !== 'profile') {
                continue
            }
            if (seen.has(mapping.inputName)) {
                continue
            }
            const sourceKey = mapping.sourceKey ?? mapping.inputName
            const supplied = context[sourceKey]
            if (supplied !== undefined && supplied !== null && supplied !== '') {
                continue
            }
            const assigned = (template.templateContext ?? []).find((c: any) => c.key === sourceKey)
            seen.add(mapping.inputName)
            result.push({
                inputName: mapping.inputName,
                sourceKey,
                provider: definition.provider,
                defaultValue: assigned?.value ?? '',
            })
        }
    }

    return result
}

/**
 * Converts either a `/skeleton` (or active-paths/error-paths) API row, or a live `step_created` SSE
 * event, into `insertStepNode`'s full parameter set. Used by every call site that inserts a step node
 * so a field (like `type`/`ordinal`) can only be added or forgotten once, in this one place, instead of
 * independently at each of the six call sites that used to hand-build this object.
 */
export function toStepNodeInsert(raw: any): {
    stepId: number
    parentFlowStepAttemptId: number | null
    label: string
    position: number
    status: string
    action: string
    type?: 'step' | 'iteration'
    ordinal?: number
    canHaveChildren?: boolean
    isIterator?: boolean
} {
    return {
        stepId: raw.stepId ?? raw.id,
        parentFlowStepAttemptId: raw.parentFlowStepAttemptId,
        label: raw.templateStep?.label ?? raw.label ?? '',
        position: raw.templateStep?.position ?? raw.position ?? 0,
        status: raw.status,
        action: raw.templateStep?.action ?? raw.action ?? '',
        // `stepType` must win: a live event's own `raw.type` is its SSE envelope discriminator
        // (e.g. 'step_created'), always truthy, which would otherwise mask the real FlowStep type
        // carried separately to avoid exactly this collision. A /skeleton row has no `stepType` at
        // all, so it correctly falls through to its own `type` field.
        type: raw.stepType ?? raw.type,
        ordinal: raw.ordinal ?? undefined,
        canHaveChildren: raw.canHaveChildren,
        isIterator: raw.isIterator,
    }
}

/**
 * Converts either a `/skeleton`-style row's attempt, or a live `attempt_created` SSE event, into
 * `insertAttemptNode`'s full parameter set — the attempt-node counterpart of `toStepNodeInsert`.
 */
export function toAttemptNodeInsert(stepId: number, raw: any): {
    stepId: number
    attemptId: number
    attemptNumber: number
    reason: string
    status?: string
    values?: Array<{ direction: string, key: string, value: string | null }>
    conditionResults?: UIConditionResult[]
    results?: any[]
} {
    return {
        stepId,
        attemptId: raw.attemptId ?? raw.id,
        attemptNumber: raw.attemptNumber,
        reason: raw.reason,
        status: raw.status,
        values: raw.values,
        conditionResults: raw.conditionResults,
        results: raw.results,
    }
}

export const useFlowsStore = defineStore('flows', {
    state: () => ({
        flows: [] as any[],
        currentFlow: null as any,
        templates: [] as any[],
        currentTemplate: null as any,
        currentTemplateAnalysis: null as { publishFindings: string[]; profileFindings: any[] } | null,
        lastTemplateMigration: null as string | null,
        actions: [] as any[],
        profiles: [] as any[],
        pendingProfilePrompt: null as PendingProfilePrompt | null,
        nodeMap: new Map<string, UITreeNode>(),
        rootNodeKeys: [] as string[],
        expandedKeys: new Set<string>(),
        sseInitialized: false,
        templateKeyRuns: {} as Record<number, TemplateKeyRun>,
        watchedTemplateKeyRunIds: new Set<number>(),
    }),

    getters: {
        rootNodes(state): UITreeNode[] {
            return state.rootNodeKeys
                .map((key) => state.nodeMap.get(key))
                .filter((node): node is UITreeNode => node !== undefined)
                .map(flattenNode)
        },

        hasFailedSteps(state): boolean {
            for (const node of state.nodeMap.values()) {
                if (node.nodeType === 'step' && node.icon === 'error') {
                    return true
                }
            }
            return false
        },
    },

    actions: {
        init(): void {
            if (this.sseInitialized) {
                return
            }
            this.sseInitialized = true

            const events = useEvents()

            const forCurrentFlow = (evt: { flowId: number }, apply: () => void): void => {
                if (evt.flowId !== this.currentFlow?.id) {
                    return
                }
                apply()
            }

            events.on('flow_status_changed', (evt: any) => {
                this.patchFlowStatus(evt.flowId, evt.status)
            })

            events.on('flow_created', async (evt: any) => {
                const flow = await $fetch(`/api/automation/flows/${evt.flowId}`, {})
                this.flows.unshift(flow)
            })

            events.on('step_status_changed', (evt: any) => forCurrentFlow(evt, () => {
                const { stepId, attemptId, status, values, conditionResults } = evt
                this.updateNodeStatus({
                    stepId,
                    attemptId: attemptId ?? null,
                    status,
                    ...(values != null && { values }),
                    ...(conditionResults != null && { conditionResults }),
                })
            }))

            events.on('step_created', (evt: any) => forCurrentFlow(evt, () => {
                this.insertStepNode(toStepNodeInsert(evt))
            }))

            events.on('attempt_created', (evt: any) => forCurrentFlow(evt, () => {
                this.insertAttemptNode(toAttemptNodeInsert(evt.stepId, evt))
            }))

            events.on('step_result', (evt: any) => forCurrentFlow(evt, () => {
                this.appendAttemptResult(evt.attemptId, evt.result)
            }))
        },

        patchFlowStatus(flowId: number, status: string): void {
            if (this.currentFlow?.id === flowId) {
                this.currentFlow.status = status
            }
            const row = this.flows.find((f: any) => f.id === flowId)
            if (row) {
                row.status = status
            }
        },

        // Iteration siblings all share their container's templateStep.position, so ordinal breaks the tie.
        insertNodeInOrder(children: UITreeNode[], node: UITreeNode): void {
            const insertIdx = children.findIndex((existingNode) => {
                if (existingNode.position !== node.position) {
                    return existingNode.position > node.position
                }
                return (existingNode.ordinal ?? 0) > (node.ordinal ?? 0)
            })
            if (insertIdx === -1) {
                children.push(node)
            }
            else {
                children.splice(insertIdx, 0, node)
            }
        },

        // A node can be orphaned when its `step_created`/`attempt_created` event arrives before its
        // parent's — e.g. a container's own creation was never announced (see createFrame), so a real
        // child's event references an attempt id nobody has heard of yet. `nodeMap.set` still stores it,
        // but it's unreachable from `rootNodes` until this rescues it once the missing parent shows up.
        rescueOrphans(parentKey: string): void {
            const parent = this.nodeMap.get(parentKey)
            if (!parent) {
                return
            }
            for (const node of this.nodeMap.values()) {
                if (node.parentKey !== parentKey) {
                    continue
                }
                if (parent.children.some((c) => c.key === node.key)) {
                    continue
                }
                if (node.nodeType === 'step') {
                    this.insertNodeInOrder(parent.children, node)
                }
                else {
                    parent.children.push(node)
                }
                this.rescueOrphans(node.key)
            }
        },

        insertStepNode(event: {
            stepId: number
            parentFlowStepAttemptId: number | null
            label: string
            position: number
            status: string
            action?: string
            canHaveChildren?: boolean
            isIterator?: boolean
            type?: 'step' | 'iteration'
            ordinal?: number
        }): void {
            const key = `step-${event.stepId}`
            const existing = this.nodeMap.get(key)
            if (existing) {
                if (!existing.action && event.action) {
                    existing.action = event.action
                }
                return
            }
            const parentKey =
                event.parentFlowStepAttemptId != null
                    ? `attempt-${event.parentFlowStepAttemptId}`
                    : null

            const label = event.type === 'iteration' ? `Iteration ${event.ordinal}` : event.label

            const node: UITreeNode = {
                key,
                label,
                position: event.position,
                ordinal: event.ordinal,
                icon: stepIcon(event.status),
                iconColor: stepIconColor(event.status),
                nodeType: 'step',
                sourceId: event.stepId,
                action: event.action ?? '',
                canHaveChildren: event.canHaveChildren,
                isIterator: event.isIterator,
                inputs: [],
                outputs: [],
                conditions: [],
                commands: stepCommands(event.status),
                header: 'step',
                body: stepBody(event.status),
                children: [],
                lazy: false,
                parentKey,
                results: [],
            }

            this.nodeMap.set(key, node)

            if (parentKey === null) {
                const insertIdx = this.rootNodeKeys.findIndex((k) => {
                    const existingNode = this.nodeMap.get(k)
                    return (existingNode?.position ?? Infinity) > event.position
                })
                if (insertIdx === -1) {
                    this.rootNodeKeys.push(key)
                }
                else {
                    this.rootNodeKeys.splice(insertIdx, 0, key)
                }
            }
            else {
                const parentAttempt = this.nodeMap.get(parentKey)
                if (parentAttempt) {
                    this.insertNodeInOrder(parentAttempt.children, node)
                }
            }

            this.rescueOrphans(key)

            if (AUTO_EXPAND_STATUSES.has(event.status)) {
                this.expandWithAncestors(key)
            }
        },

        insertAttemptNode(event: {
            stepId: number
            attemptId: number
            attemptNumber: number
            reason: string
            status?: string
            values?: Array<{ direction: string; key: string; value: string | null }>
            conditionResults?: UIConditionResult[]
            results?: any[]
        }): void {
            const key = `attempt-${event.attemptId}`
            if (this.nodeMap.has(key)) {
                return
            }
            const parentKey = `step-${event.stepId}`
            const isRetry = event.reason === 'auto_retry' || event.reason === 'user_retry'
            const attemptLabel = isRetry
                ? `Retry ${event.attemptNumber}`
                : `Attempt ${event.attemptNumber}`
            const status = event.status ?? 'pending'
            const { inputs, outputs } = splitValues(event.values ?? [])

            const node: UITreeNode = {
                key,
                label: attemptLabel,
                position: 0,
                icon: attemptIcon(status),
                iconColor: stepIconColor(status),
                nodeType: 'attempt',
                sourceId: event.attemptId,
                inputs,
                outputs,
                conditions: event.conditionResults ?? [],
                commands: [],
                header: 'attempt',
                body: 'attempt',
                children: [],
                lazy: false,
                parentKey,
                results: event.results ?? [],
            }

            this.nodeMap.set(key, node)

            const stepNode = this.nodeMap.get(parentKey)
            if (stepNode) {
                if (event.attemptNumber === 2 && stepNode.children.length > 0) {
                    this.expandedKeys.add(stepNode.children[0].key)
                }
                stepNode.children.push(node)
            }

            this.rescueOrphans(key)
        },

        updateNodeStatus(event: {
            stepId: number
            attemptId: number | null
            status: string
            values?: Array<{ direction: string; key: string; value: string | null }>
            conditionResults?: UIConditionResult[]
        }): void {
            const stepNode = this.nodeMap.get(`step-${event.stepId}`)
            const attemptNode = event.attemptId != null ? this.nodeMap.get(`attempt-${event.attemptId}`) : undefined

            if (stepNode) {
                stepNode.icon = stepIcon(event.status)
                stepNode.iconColor = stepIconColor(event.status)
                stepNode.commands = stepCommands(event.status)
            }
            if (attemptNode) {
                attemptNode.icon = attemptIcon(event.status)
                attemptNode.iconColor = stepIconColor(event.status)
            }

            const valuesTarget = attemptNode ?? stepNode
            if (valuesTarget) {
                if (event.values != null) {
                    const newInputs = event.values.filter((v) => v.direction === 'input')
                    const newOutputs = event.values.filter((v) => v.direction === 'output' && v.key !== '__prompt__')
                    if (newInputs.length > 0) {
                        valuesTarget.inputs = newInputs.map((v) => ({ key: v.key, value: v.value }))
                    }
                    if (newOutputs.length > 0) {
                        valuesTarget.outputs = newOutputs.map((v) => ({ key: v.key, value: v.value }))
                    }
                }
                if (event.conditionResults != null) {
                    valuesTarget.conditions = event.conditionResults
                }
                const hasValues = valuesTarget.inputs.length > 0 || valuesTarget.outputs.length > 0 || valuesTarget.conditions.length > 0
                if (hasValues) {
                    valuesTarget.body = valuesTarget.nodeType === 'attempt' ? 'attempt' : 'step'
                }
                else {
                    valuesTarget.body = stepBody(event.status)
                }
            }
        },

        appendAttemptResult(attemptId: number, result: any): void {
            const node = this.nodeMap.get(`attempt-${attemptId}`)
            if (node) {
                node.results.push(result)
            }
        },

        expandWithAncestors(key: string): void {
            this.expandedKeys.add(key)
            let current = this.nodeMap.get(key)
            while (current?.parentKey) {
                this.expandedKeys.add(current.parentKey)
                current = this.nodeMap.get(current.parentKey)
            }
        },

        clearFlowTree(): void {
            this.nodeMap.clear()
            this.rootNodeKeys = []
            this.expandedKeys.clear()
        },

        async expandErrorPaths(flowId: number): Promise<void> {
            await this.loadErrorPaths(flowId)
            for (const node of this.nodeMap.values()) {
                if (node.nodeType === 'step' && node.icon === 'error') {
                    this.expandedKeys.add(node.key)
                    let parentKey = node.parentKey
                    while (parentKey) {
                        this.expandedKeys.add(parentKey)
                        parentKey = this.nodeMap.get(parentKey)?.parentKey ?? null
                    }
                }
            }
        },

        async expandActivePaths(flowId: number): Promise<void> {
            const { active, ancestors } = await $fetch<{ active: any[]; ancestors: any[] }>(
                `/api/automation/flows/${flowId}/active-paths`,
            )
            const remaining = new Map([...ancestors, ...active].map((s: any) => [s.id, s]))
            while (remaining.size > 0) {
                let inserted = 0
                for (const [stepId, step] of remaining) {
                    if (
                        step.parentFlowStepAttemptId != null &&
                        !this.nodeMap.has(`attempt-${step.parentFlowStepAttemptId}`)
                    ) {
                        continue
                    }
                    const attempts = step.attempts ?? []
                    if (!this.nodeMap.has(`step-${stepId}`)) {
                        this.insertStepNode(toStepNodeInsert(step))
                    }
                    for (const attempt of attempts) {
                        if (!this.nodeMap.has(`attempt-${attempt.id}`)) {
                            this.insertAttemptNode(toAttemptNodeInsert(step.id, attempt))
                        }
                    }
                    remaining.delete(stepId)
                    inserted++
                }
                if (inserted === 0) {
                    break
                }
            }
            for (const step of active) {
                const key = `step-${step.id}`
                if (this.nodeMap.has(key)) {
                    this.expandWithAncestors(key)
                }
            }
        },

        collapseAll(): void {
            this.expandedKeys.clear()
        },

        async expandAllSteps(flowId: number): Promise<void> {
            const skeleton = await $fetch<any[]>(`/api/automation/flows/${flowId}/skeleton`)
            const remaining = new Map(skeleton.map((s: any) => [s.id, s]))
            while (remaining.size > 0) {
                let inserted = 0
                for (const [stepId, step] of remaining) {
                    if (
                        step.parentFlowStepAttemptId != null &&
                        !this.nodeMap.has(`attempt-${step.parentFlowStepAttemptId}`)
                    ) {
                        continue
                    }
                    const attempts = step.attempts ?? []
                    if (!this.nodeMap.has(`step-${stepId}`)) {
                        this.insertStepNode(toStepNodeInsert(step))
                    }
                    for (const attempt of attempts) {
                        if (!this.nodeMap.has(`attempt-${attempt.id}`)) {
                            this.insertAttemptNode(toAttemptNodeInsert(step.id, attempt))
                        }
                    }
                    remaining.delete(stepId)
                    inserted++
                }
                if (inserted === 0) {
                    break
                }
            }
            for (const node of this.nodeMap.values()) {
                node.lazy = false
                this.expandedKeys.add(node.key)
            }
        },

        async loadSettledSkeleton(flowId: number): Promise<void> {
            if (this.rootNodeKeys.length > 0) {
                return
            }
            const skeleton = await $fetch<any[]>(`/api/automation/flows/${flowId}/skeleton`)
            const roots = skeleton.filter((step: any) => step.parentFlowStepAttemptId == null)
            for (const step of roots) {
                const attempts = step.attempts ?? []
                this.insertStepNode(toStepNodeInsert({ ...step, parentFlowStepAttemptId: null }))
                const hasChildren = skeleton.some((candidate: any) => {
                    if (candidate.parentFlowStepAttemptId == null) {
                        return false
                    }
                    return attempts.some((attempt: any) => attempt.id === candidate.parentFlowStepAttemptId)
                })
                const rootNode = this.nodeMap.get(`step-${step.id}`)
                if (rootNode && hasChildren) {
                    rootNode.lazy = true
                }
                for (const attempt of attempts) {
                    this.insertAttemptNode(toAttemptNodeInsert(step.id, attempt))
                }
            }
        },

        async loadStepChildren(flowId: number, stepId: number): Promise<void> {
            const skeleton = await $fetch<any[]>(`/api/automation/flows/${flowId}/skeleton`)
            const stepNode = this.nodeMap.get(`step-${stepId}`)
            if (!stepNode) {
                return
            }
            const attemptIds = new Set(
                stepNode.children
                    .filter((child) => child.nodeType === 'attempt')
                    .map((child) => child.sourceId),
            )
            for (const child of skeleton) {
                if (child.parentFlowStepAttemptId == null) {
                    continue
                }
                if (!attemptIds.has(child.parentFlowStepAttemptId)) {
                    continue
                }
                if (this.nodeMap.has(`step-${child.id}`)) {
                    const existing = this.nodeMap.get(`step-${child.id}`)!
                    if (!existing.action && child.templateStep?.action) {
                        existing.action = child.templateStep.action
                    }
                    continue
                }
                const childAttempts = child.attempts ?? []
                this.insertStepNode(toStepNodeInsert(child))
                const childAttemptIds = new Set(
                    childAttempts.map((a: any) => a.id),
                )
                const hasGrandchildren = skeleton.some(
                    (candidate: any) =>
                        candidate.parentFlowStepAttemptId != null &&
                        childAttemptIds.has(candidate.parentFlowStepAttemptId),
                )
                const childNode = this.nodeMap.get(`step-${child.id}`)
                if (childNode && hasGrandchildren) {
                    childNode.lazy = true
                }
                for (const attempt of childAttempts) {
                    this.insertAttemptNode(toAttemptNodeInsert(child.id, attempt))
                }
            }
        },

        async loadErrorPaths(flowId: number): Promise<void> {
            const { failed, ancestors } = await $fetch<{ failed: any[]; ancestors: any[] }>(
                `/api/automation/flows/${flowId}/error-paths`,
            )
            for (const step of [...ancestors, ...failed]) {
                const attempts = step.attempts ?? []
                this.insertStepNode(toStepNodeInsert(step))
                for (const attempt of attempts) {
                    if (!this.nodeMap.has(`attempt-${attempt.id}`)) {
                        this.insertAttemptNode(toAttemptNodeInsert(step.id, attempt))
                    }
                }
            }
            for (const step of failed) {
                const stepNode = this.nodeMap.get(`step-${step.id}`)
                if (!stepNode) {
                    continue
                }
                const lastAttempt = step.attempts?.at(-1)
                const lastResult = lastAttempt?.results?.at(-1)
                if (lastResult?.message) {
                    stepNode.errorMessage = lastResult.message
                    stepNode.body = 'step'
                }
            }
        },

        async fetchFlows(filters: { status?: string } = {}) {
            const params: Record<string, string> = {}
            if (filters.status) {
                params.status = filters.status
            }
            const qs = Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
            await useRequest({
                label: 'fetch flows',
                fetch: () => $fetch(`/api/automation/flows${qs}`, {
                }),
                evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
                store: (data) => {
                    this.flows = data
                },
            })
        },

        async fetchFlow(id: number) {
            await useRequest({
                label: `fetch flow ${id}`,
                fetch: () => $fetch(`/api/automation/flows/${id}`, {
                }),
                evaluate: (data: any) => data,
                store: (data) => {
                    this.currentFlow = data
                },
            })
        },

        async cancelFlow(id: number, propagate = false) {
            await useRequest({
                label: `cancel flow ${id}`,
                fetch: () => $fetch(`/api/automation/flows/${id}/cancel`, {
                    method: 'POST',
                }),
                evaluate: (data: any) => data,
                propagate,
            })
        },

        async submitStep(flowId: number, stepId: number, outputs: Record<string, string>) {
            await useRequest({
                label: `submit step ${stepId}`,
                fetch: () => $fetch(`/api/automation/flows/${flowId}/steps/${stepId}/submit`, {
                    method: 'POST',
                    body: { outputs },
                }),
                evaluate: (data: any) => data,
            })
        },

        async retryStep(flowId: number, stepId: number) {
            await useRequest({
                label: `retry step ${stepId}`,
                fetch: () => $fetch(`/api/automation/flows/${flowId}/steps/${stepId}/retry`, {
                    method: 'POST',
                }),
                evaluate: (data: any) => data,
            })
        },

        async skipStep(flowId: number, stepId: number) {
            await useRequest({
                label: `skip step ${stepId}`,
                fetch: () => $fetch(`/api/automation/flows/${flowId}/steps/${stepId}/skip`, {
                    method: 'POST',
                }),
                evaluate: (data: any) => data,
            })
        },

        async startFlow(body: {
            templateId?: number,
            templateName?: string,
            context: Record<string, any>,
            settings?: { key: string; value: string }[],
        }, propagate = false): Promise<{ flowId: number } | undefined> {
            return await useRequest({
                label: 'start flow',
                fetch: () => $fetch('/api/automation/flows', {
                    method: 'POST',
                    body,
                }),
                evaluate: (data: any) => data,
                propagate,
            }) as { flowId: number } | undefined
        },

        /**
         * Trigger-time entry point used by UI buttons (via their own store).
         * Shows the profile popup only when the template has an unset
         * `binding = flow` profile input; otherwise initiates directly. When a
         * template cannot be inspected, it initiates directly and relies on the
         * server-side preconditions / `buildCredentials` backstop.
         */
        async triggerFlow(opts: {
            templateId?: number,
            templateName?: string,
            template?: any,
            context?: Record<string, any>,
            propagate?: boolean,
            settings?: { key: string; value: string }[],
        }): Promise<{ flowId: number } | undefined> {
            const context = { ...(opts.context ?? {}) }
            const body = { templateId: opts.templateId, templateName: opts.templateName, settings: opts.settings }

            if (!this.actions || this.actions.length === 0) {
                await this.fetchActions()
            }

            const unset = opts.template
                ? computeUnsetFlowProfiles(opts.template, this.actions, context)
                : []

            if (unset.length === 0) {
                return await this.startFlow({ ...body, context }, opts.propagate)
            }

            return await new Promise<{ flowId: number } | undefined>((resolve) => {
                this.pendingProfilePrompt = {
                    inputs: unset,
                    body,
                    context,
                    propagate: opts.propagate ?? false,
                    resolve,
                }
            })
        },

        async submitTriggerProfiles(selections: Record<string, string>): Promise<void> {
            const prompt = this.pendingProfilePrompt
            if (!prompt) {
                return
            }
            const context = { ...prompt.context }
            for (const input of prompt.inputs) {
                context[input.sourceKey] = Number(selections[input.inputName])
            }
            this.pendingProfilePrompt = null
            const result = await this.startFlow({ ...prompt.body, context }, prompt.propagate)
            prompt.resolve(result)
        },

        cancelTriggerProfiles() {
            const prompt = this.pendingProfilePrompt
            this.pendingProfilePrompt = null
            prompt?.resolve(undefined)
        },

        async fetchLastTemplateMigration() {
            await useRequest({
                label: 'fetch last template migration',
                fetch: () => $fetch('/api/automation/templates/migrations/last'),
                evaluate: (data: any) => data?.name ?? null,
                store: (data) => {
                    this.lastTemplateMigration = data
                },
            })
        },

        async fetchTemplates(filters: { status?: string; search?: string; type?: FlowTemplateType } = {}) {
            const params: Record<string, string> = {}
            if (filters.status) {
                params.status = filters.status
            }
            if (filters.search) {
                params.search = filters.search
            }
            if (filters.type) {
                params.type = filters.type
            }
            const qs = Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
            await useRequest({
                label: 'fetch templates',
                fetch: () => $fetch(`/api/automation/templates${qs}`, {
                }),
                evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
                store: (data) => {
                    this.templates = data
                },
            })
        },

        async fetchTemplate(id: number) {
            await useRequest({
                label: `fetch template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}`, {
                }),
                evaluate: (data: any) => data,
                store: (data) => {
                    this.currentTemplate = data
                },
            })
        },

        async fetchTemplateAnalysis(id: number): Promise<void> {
            const data = await $fetch<{ publishFindings: string[]; profileFindings: any[] }>(
                `/api/automation/templates/${id}/analysis`,
            )
            this.currentTemplateAnalysis = data
        },

        async loadTemplate(id: number): Promise<any> {
            return await useRequest({
                label: `load template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}`, {
                }),
                evaluate: (data: any) => data,
            })
        },

        async createTemplate(data: { name: string; label: string; type?: FlowTemplateType }, propagate = false) {
            return await useRequest({
                label: 'create template',
                fetch: () => $fetch('/api/automation/templates', {
                    method: 'POST',
                    body: data,
                }),
                evaluate: (d: any) => d,
                propagate,
            })
        },

        async saveTemplate(id: number, data: any) {
            await useRequest({
                label: `save template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}`, {
                    method: 'PUT',
                    body: data,
                }),
                evaluate: (d: any) => d,
            })
        },

        watchTemplateKeyRuns(templateIds: number[]): void {
            const events = useEvents()

            for (const templateId of templateIds) {
                if (!this.templateKeyRuns[templateId]) {
                    this.templateKeyRuns[templateId] = emptyTemplateKeyRun()
                }
                if (this.watchedTemplateKeyRunIds.has(templateId)) {
                    continue
                }
                this.watchedTemplateKeyRunIds.add(templateId)

                events.on(flowTemplateEventType(templateId, FLOW_TEMPLATE_EVENT_NAMES.stepKeyApplied), () => {
                    this.templateKeyRuns[templateId]!.applied++
                })

                events.on(flowTemplateEventType(templateId, FLOW_TEMPLATE_EVENT_NAMES.stepKeyConflict), (evt: any) => {
                    this.templateKeyRuns[templateId]!.conflicts.push({
                        stepId: evt.stepId,
                        keys: evt.keys,
                    })
                })

                events.on(flowTemplateEventType(templateId, FLOW_TEMPLATE_EVENT_NAMES.stepKeysCompleted), async (evt: any) => {
                    const run = this.templateKeyRuns[templateId]!
                    run.running = false
                    run.applied = evt.applied
                    run.skipped = evt.skipped

                    if (this.currentTemplate?.id === templateId) {
                        await this.fetchTemplate(templateId)
                    }
                })
            }
        },

        async generateTemplateKeys(templateIds: number[]): Promise<void> {
            this.watchTemplateKeyRuns(templateIds)
            for (const templateId of templateIds) {
                this.templateKeyRuns[templateId] = {
                    ...emptyTemplateKeyRun(),
                    running: true,
                }
            }

            await useRequest({
                label: `generate keys for templates ${templateIds.join(', ')}`,
                fetch: () => $fetch('/api/automation/templates/generate-keys', {
                    method: 'POST',
                    body: { templateIds },
                }),
                evaluate: (d: any) => d,
                propagate: true,
            })
        },

        async importTemplate(file: unknown, replace = false): Promise<{ jobId: string }> {
            return await useRequest({
                label: 'import template',
                fetch: () => $fetch('/api/automation/templates/import', {
                    method: 'POST',
                    body: { file, replace },
                }),
                evaluate: (d: any) => d,
                propagate: true,
            }) as { jobId: string }
        },

        async exportTemplate(templateId: number): Promise<void> {
            await useRequest({
                label: `export template ${templateId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/export`, {
                    method: 'POST',
                }),
                evaluate: (d: any) => d,
                propagate: true,
            })
        },

        async bumpTemplate(id: number): Promise<{ id: number }> {
            return await useRequest({
                label: `bump template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}/bump`, {
                    method: 'POST',
                }),
                evaluate: (d: any) => d,
            }) as { id: number }
        },

        async deleteTemplate(id: number) {
            await useRequest({
                label: `delete template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}`, {
                    method: 'DELETE',
                }),
                evaluate: (d: any) => d,
                propagate: true,
            })
        },

        async publishTemplate(id: number) {
            await useRequest({
                label: `publish template ${id}`,
                fetch: () => $fetch(`/api/automation/templates/${id}/publish`, {
                    method: 'POST',
                }),
                evaluate: (d: any) => d,
                propagate: true,
            })
        },

        async fetchActions() {
            await useRequest({
                label: 'fetch actions',
                fetch: () => $fetch('/api/automation/flows/actions', {
                }),
                evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
                store: (data) => {
                    this.actions = data
                },
            })
        },

        async fetchProfiles() {
            await useRequest({
                label: 'fetch flow profiles',
                fetch: () => $fetch('/api/automation/flows/profiles', {
                }),
                evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
                store: (data) => {
                    this.profiles = data
                },
            })
        },

        async fetchAvailableProfiles(providerName: string): Promise<{ id: number, label: string }[]> {
            return await useRequest({
                label: `fetch available profiles for ${providerName}`,
                fetch: () => $fetch<{ id: number, label: string }[]>('/api/automation/flows/available-profiles', {
                    query: { providerName },
                }),
                evaluate: (data: any) => data,
            }) as { id: number, label: string }[]
        },

        async createCollection(templateId: number, name: string, type: string, valueType: string): Promise<{ id: number }> {
            return await useRequest({
                label: `create collection ${name}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/collections`, {
                    method: 'POST',
                    body: {
                        name,
                        type,
                        valueType,
                    },
                }),
                evaluate: (data: any) => data,
            }) as { id: number }
        },

        async deleteCollection(templateId: number, collectionId: number): Promise<void> {
            await useRequest({
                label: `delete collection ${collectionId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/collections/${collectionId}`, {
                    method: 'DELETE',
                }),
                evaluate: (data: any) => data,
            })
        },

        async addCollectionMapEntry(templateId: number, collectionId: number, key: string, value: string): Promise<void> {
            await useRequest({
                label: `add map entry to collection ${collectionId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/collections/${collectionId}/elements`, {
                    method: 'POST',
                    body: {
                        key,
                        value,
                    },
                }),
                evaluate: (data: any) => data,
            })
        },

        async addCollectionSetElement(
            templateId: number,
            collectionId: number,
            value: { stringValue?: string; profileId?: number },
        ): Promise<void> {
            await useRequest({
                label: `add set element to collection ${collectionId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/collections/${collectionId}/elements`, {
                    method: 'POST',
                    body: value,
                }),
                evaluate: (data: any) => data,
            })
        },

        async updateCollectionElement(
            templateId: number,
            collectionId: number,
            elementId: number,
            key: string,
            value: string,
        ): Promise<void> {
            await useRequest({
                label: `update collection ${collectionId} element ${elementId}`,
                fetch: () => $fetch(
                    `/api/automation/templates/${templateId}/collections/${collectionId}/elements/${elementId}`,
                    {
                        method: 'PUT',
                        body: {
                            key,
                            value,
                        },
                    },
                ),
                evaluate: (data: any) => data,
            })
        },

        async deleteCollectionElement(templateId: number, collectionId: number, elementId: number): Promise<void> {
            await useRequest({
                label: `delete collection ${collectionId} element ${elementId}`,
                fetch: () => $fetch(
                    `/api/automation/templates/${templateId}/collections/${collectionId}/elements/${elementId}`,
                    { method: 'DELETE' },
                ),
                evaluate: (data: any) => data,
            })
        },

        async createTemplateProfile(templateId: number, data: {
            providerId?: number | null
            identityId?: number | null
            profileId?: number | null
        }): Promise<any> {
            return await useRequest({
                label: `create template ${templateId} profile`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/profiles`, {
                    method: 'POST',
                    body: data,
                }),
                evaluate: (d: any) => d,
            })
        },

        async updateTemplateProfile(templateId: number, rowId: number, data: {
            identityId?: number | null
            profileId?: number | null
        }): Promise<void> {
            await useRequest({
                label: `update template ${templateId} profile ${rowId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/profiles/${rowId}`, {
                    method: 'PATCH',
                    body: data,
                }),
                evaluate: (d: any) => d,
            })
        },

        async deleteTemplateProfile(templateId: number, rowId: number): Promise<void> {
            await useRequest({
                label: `delete template ${templateId} profile ${rowId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/profiles/${rowId}`, {
                    method: 'DELETE',
                }),
                evaluate: (d: any) => d,
            })
        },

        async upsertTemplateSetting(templateId: number, data: {
            key: string
            value: string | null
            level?: string
            required?: boolean
        }): Promise<void> {
            await useRequest({
                label: `upsert template ${templateId} setting ${data.key}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/settings`, {
                    method: 'POST',
                    body: [data],
                }),
                evaluate: (d: any) => d,
            })
        },

        async deleteTemplateSetting(templateId: number, settingId: number): Promise<void> {
            await useRequest({
                label: `delete template ${templateId} setting ${settingId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/settings/${settingId}`, {
                    method: 'DELETE',
                }),
                evaluate: (d: any) => d,
            })
        },

        async createTemplateSchedule(templateId: number, cronExpression: string): Promise<any> {
            return await useRequest({
                label: `create template ${templateId} schedule`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/schedules`, {
                    method: 'POST',
                    body: { cronExpression },
                }),
                evaluate: (d: any) => d,
            })
        },

        async updateTemplateSchedule(templateId: number, scheduleId: number, cronExpression: string): Promise<any> {
            return await useRequest({
                label: `update template ${templateId} schedule ${scheduleId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/schedules/${scheduleId}`, {
                    method: 'PATCH',
                    body: { cronExpression },
                }),
                evaluate: (d: any) => d,
            })
        },

        async deleteTemplateSchedule(templateId: number, scheduleId: number): Promise<void> {
            await useRequest({
                label: `delete template ${templateId} schedule ${scheduleId}`,
                fetch: () => $fetch(`/api/automation/templates/${templateId}/schedules/${scheduleId}`, {
                    method: 'DELETE',
                }),
                evaluate: (d: any) => d,
            })
        },

        async startTemporalSchedule(scheduleId: number): Promise<void> {
            await useRequest({
                label: `start temporal schedule ${scheduleId}`,
                fetch: () => $fetch('/api/temporal/schedules/start', {
                    method: 'POST',
                    body: { scheduleId },
                }),
                evaluate: (d: any) => d,
            })
        },

        async stopTemporalSchedule(scheduleId: number): Promise<void> {
            await useRequest({
                label: `stop temporal schedule ${scheduleId}`,
                fetch: () => $fetch('/api/temporal/schedules/stop', {
                    method: 'POST',
                    body: { scheduleId },
                }),
                evaluate: (d: any) => d,
            })
        },

        async executeTemplateNow(templateId: number): Promise<void> {
            await useRequest({
                label: `execute template ${templateId} now`,
                fetch: () => $fetch('/api/temporal/schedules/execute', {
                    method: 'POST',
                    body: { templateId },
                }),
                evaluate: (d: any) => d,
            })
        },
    },
})
